import { useEffect, useRef, useState } from 'react';
import { Button, Sheet } from '../../components';
import { useI18n } from '../../lib/i18n';

/** Finger/stylus signature captured to a PNG. */
export function SignaturePad({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (png: Blob) => void }) {
  const { t } = useI18n();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    if (!open) return;
    const c = canvas.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.clientWidth * ratio;
    c.height = c.clientHeight * ratio;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0e1116';
    setEmpty(true);
  }, [open]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('ui.capture.signature')}
      closeLabel={t('ui.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              const c = canvas.current;
              c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
              setEmpty(true);
            }}
          >
            {t('ui.common.clear')}
          </Button>
          <Button
            block
            disabled={empty}
            onClick={() => canvas.current?.toBlob((b) => b && onSave(b), 'image/png')}
          >
            {t('ui.common.save')}
          </Button>
        </>
      }
    >
      <canvas
        ref={canvas}
        className="h-56 w-full touch-none rounded-xl border-2 border-dashed border-border bg-white"
        onPointerDown={(e) => {
          drawing.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          const ctx = e.currentTarget.getContext('2d');
          const p = point(e);
          ctx?.beginPath();
          ctx?.moveTo(p.x, p.y);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = e.currentTarget.getContext('2d');
          const p = point(e);
          ctx?.lineTo(p.x, p.y);
          ctx?.stroke();
          setEmpty(false);
        }}
        onPointerUp={() => (drawing.current = false)}
      />
    </Sheet>
  );
}
